/**
 * Pinia store for car/vehicle data management
 *
 * Follows sde-client pattern: direct API calls in store actions.
 * BE field names now match FE - no mapping needed.
 *
 * BE API Namespace: api.cars
 */

import { defineStore } from 'pinia'
import { type Car, type RentalCarEvent } from '~/api/types/car'

/**
 * Helper to convert date to timestamp for BE
 */
const toTimestamp = (value: unknown): number | null => {
  if (!value) return null
  if (typeof value === 'number') return value
  if (typeof value === 'string') return new Date(value).getTime()
  if (value instanceof Date) return value.getTime()
  return null
}

/**
 * Convert a timestamp (ms) to YYYY-MM-DD date string.
 * Returns null if value is falsy or not a number.
 */
const timestampToDateStr = (value: unknown): string | null => {
  if (typeof value === 'number' && value > 0) {
    return new Date(value).toISOString().slice(0, 10)
  }
  if (typeof value === 'string' && value) return value
  return null
}

/** Date fields that come from BE as timestamps and should be stored as YYYY-MM-DD strings */
const DATE_FIELDS = new Set([
  'tyreChangingAppointment',
  'leasingStartDate',
  'leasingEndDate',
  'stkExpiration',
  'vignetteExpirationDate',
])

/**
 * Normalize BE response:
 * 1. Map lowercase field names to camelCase
 * 2. Convert timestamp date fields to YYYY-MM-DD strings
 */
const normalizeBEFields = (car: Record<string, unknown>): void => {
  const fieldMap: Record<string, string> = {
    tyrechangingappointment: 'tyreChangingAppointment',
    contractnumber: 'contractNumber',
    licenceplate: 'licencePlate',
    fueltype: 'fuelType',
    fuellevel: 'fuelLevel',
    carcondition: 'carCondition',
    statusnote: 'statusNote',
    leasingcompany: 'leasingCompany',
    leasingstartdate: 'leasingStartDate',
    leasingenddate: 'leasingEndDate',
    allowedyearlykm: 'allowedYearlyKm',
    kmatyearstart: 'kmAtYearStart',
    stkexpiration: 'stkExpiration',
    vignetteexpirationdate: 'vignetteExpirationDate',
    sparekey: 'spareKey',
    monthlyrate: 'monthlyRate',
    dailyrate: 'dailyRate',
    totalrevenue: 'totalRevenue',
    windowsinsurance: 'windowsInsurance',
    technicalpapersurl: 'technicalPapersUrl',
    agreementpapersurl: 'agreementPapersUrl',
    gpslatitude: 'gpsLatitude',
    gpslongitude: 'gpsLongitude',
    manualaddress: 'manualAddress',
    insuranceid: 'insuranceId',
    partnerid: 'partnerId',
    rentedtocaseid: 'rentedToCaseId',
    customdata: 'customData',
    groupid: 'groupId',
    shortterm: 'shortTerm',


  }
  for (const [lower, camel] of Object.entries(fieldMap)) {
    if (lower in car && !(camel in car)) {
      car[camel] = car[lower]
      delete car[lower]
    }
  }

  // Convert timestamp date fields to YYYY-MM-DD strings
  for (const field of DATE_FIELDS) {
    if (field in car && typeof car[field] === 'number') {
      car[field] = timestampToDateStr(car[field])
    }
  }
}

/**
 * Parse customData from BE response (may be JSON string or object)
 */
const parseCustomData = (car: Car): Car => {
  normalizeBEFields(car as unknown as Record<string, unknown>)
  if (car.customData && typeof car.customData === 'string') {
    try {
      car.customData = JSON.parse(car.customData)
    } catch (e) {
      console.warn('[carStore] Failed to parse customData:', e)
      car.customData = undefined
    }
  }
  return car
}

/**
 * Fetch a single car from API and cache it in the provided cars record.
 * Module-level helper — not exposed as a public store action.
 */
const fetchAndCacheCar = async (
  api: API,
  id: string,
  cars: Record<string, Car>
): Promise<Car | null> => {
  const response = await api.cars.getRentalCar(id)
  const car = parseCustomData(response as Car)
  cars[car.id] = car
  return car
}

export const useCarStore = defineStore({
  id: 'cars',

  state: () => ({
    cars: {} as Record<string, Car>,
    rentalCarEvents: {} as Record<string, RentalCarEvent[]>,
    loading: false,
    initialized: false
  }),

  getters: {
    allCars: (state) => Object.values(state.cars),
    // availableCars kept as array — used by StepRentedVehicleInfo for car selection
    availableCars: (state) => Object.values(state.cars).filter(c => c.status === 'AVAILABLE'),
    rentedCount: (state) => {
      let count = 0
      for (const c of Object.values(state.cars)) {
        if (c.status === 'RENTED') count++
      }
      return count
    },
    car: (state) => (id: string) => state.cars[id],
  },

  actions: {
    /**
     * Fetch all cars from API
     */
    async fetchCars(api: API) {
      if (!api?.cars) {
        console.warn('[carStore] api.cars not available')
        return
      }

      this.loading = true

      try {
        const response = await api.cars.getAllRentalCars()
        const fetched = (response.value || []) as Car[]

        const newCars: Record<string, Car> = {}
        for (const c of fetched) {
          newCars[c.id] = parseCustomData(c)
        }

        this.cars = newCars
        this.initialized = true
      } catch (e) {
        console.error('[carStore] Failed to fetch cars:', e)
      } finally {
        this.loading = false
      }
    },

    /**
     * Create a new car
     * BE now uses FE field names directly
     */
    async create(api: API, car: Omit<Car, 'id'>): Promise<Car> {
      if (!api?.cars) {
        throw new Error('Car API not available')
      }

      this.loading = true

      try {
        const response = await api.cars.createRentalCar(
          car.brand || '',                          // 1. brand (required)
          car.type || '',                           // 2. type (required)
          car.licencePlate || '',                   // 3. licencePlate (required)
          car.transmission || 'MANUAL',             // 4. transmission (required)
          car.color || '',                          // 5. color (required)
          car.tyres || 'SUMMER',                    // 6. tyres (required)
          car.mileage ?? 0,                         // 7. mileage (required)
          car.dailyRate ?? 0,                       // 8. dailyRate
          car.status === 'AVAILABLE',               // 9. available (required) - legacy boolean
          car.vin || '',                            // 10. vin (required)
          car.fuelLevel || 'FULL',                  // 11. fuelLevel
          car.insuranceId || null,                  // 12. insurance (UUID)
          car.partnerId || null,                    // 13. partner (UUID)
          car.leasingCompany || null,               // 14. leasingCompany
          toTimestamp(car.leasingStartDate),        // 15. leasingStartDate
          toTimestamp(car.leasingEndDate),          // 16. leasingEndDate
          car.allowedYearlyKm ?? 0,                 // 17. allowedYearlyKm
          car.carCondition || 'GOOD',               // 18. carCondition
          car.deductible ?? 5,                      // 19. deductible (required)
          car.windowsInsurance ?? false,            // 20. windowsInsurance (required)
          car.vignette ?? false,                    // 21. vignette
          toTimestamp(car.vignetteExpirationDate),  // 22. vignetteExpirationDate
          car.spareKey || null,                     // 23. spareKey
          car.fuelType || 'PETROL',                 // 24. fuelType (required)
          car.monthlyRate ?? 0,                     // 25. monthlyRate
          toTimestamp(car.stkExpiration),           // 26. stkExpiration
          car.note || '',                           // 27. note
          car.service || '',                        // 28. service
          car.statusNote || '',                     // 29. statusNote
          car.gpsLatitude ?? 0,                     // 30. gpsLatitude (int, not Integer)
          car.gpsLongitude ?? 0,                    // 31. gpsLongitude (int, not Integer)
          car.manualAddress || null,                // 32. manualAddress
          car.technicalPapersUrl || null,           // 33. technicalPapersID (UUID)
          car.agreementPapersUrl || null,           // 34. agreementPapersID (UUID)
          car.kmAtYearStart ?? 0,                   // 35. kmAtYearStart
          car.status || 'AVAILABLE',                // 36. status (CarStatus enum)
          toTimestamp(car.tyreChangingAppointment),  // 37. tyreChangingAppointment
          car.contractNumber || null,                // 38. contractNumber                                           
          car.groupId || null,                       // 39. groupId
          car.shortTerm ?? false,                    // 40. shortTerm
          null                                       // 41. customData

        )

        const newCar = response as Car

        const initialStatus = car.status || 'AVAILABLE'
        await api.cars.createRentalCarEvent(newCar.id, 'STATUS_CHANGE',
          JSON.stringify({
            fromStatus: null,
            toStatus: initialStatus,
            shortTerm: !!car.shortTerm
          }), Date.now(), null
        )

        // Fetch fresh data from BE to ensure state consistency
        const freshCar = await fetchAndCacheCar(api, newCar.id, this.cars)
        return freshCar || newCar
      } catch (e) {
        console.error('[carStore] Failed to create car:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    /**
     * Update an existing car
     * BE expects positional parameters (same as create, but with id first)
     */
    async update(api: API, id: string, updates: Partial<Car>): Promise<Car | null> {
      if (!api?.cars) {
        throw new Error('Car API not available')
      }

      this.loading = true

      try {
        // Merge with existing car data
        const existing = this.cars[id]
        const merged = { ...existing, ...updates }

        const previousStatus = existing?.status
        const nextStatus = merged.status
        const statusChanged = !!previousStatus && !!nextStatus && previousStatus !== nextStatus

        await api.cars.updateRentalCar(
          id,                                       // 0. id (required)
          merged.brand || '',                       // 1. brand (required)
          merged.type || '',                        // 2. type (required)
          merged.licencePlate || '',                // 3. licencePlate (required)
          merged.transmission || 'MANUAL',          // 4. transmission (required)
          merged.color || '',                       // 5. color (required)
          merged.tyres || 'SUMMER',                 // 6. tyres (required)
          merged.mileage ?? 0,                      // 7. mileage (required)
          merged.dailyRate ?? 0,                    // 8. dailyRate
          merged.status === 'AVAILABLE',            // 9. available (required) - legacy boolean
          merged.vin || '',                         // 10. vin (required)
          merged.fuelLevel || 'FULL',               // 11. fuelLevel
          merged.insuranceId || null,               // 12. insurance (UUID)
          merged.partnerId || null,                 // 13. partner (UUID)
          merged.leasingCompany || null,            // 14. leasingCompany
          toTimestamp(merged.leasingStartDate),     // 15. leasingStartDate
          toTimestamp(merged.leasingEndDate),       // 16. leasingEndDate
          merged.allowedYearlyKm ?? 0,              // 17. allowedYearlyKm
          merged.carCondition || 'GOOD',            // 18. carCondition
          merged.deductible ?? 5,                   // 19. deductible (required)
          merged.windowsInsurance ?? false,         // 20. windowsInsurance (required)
          merged.vignette ?? false,                 // 21. vignette
          toTimestamp(merged.vignetteExpirationDate), // 22. vignetteExpirationDate
          merged.spareKey || null,                  // 23. spareKey
          merged.fuelType || 'PETROL',              // 24. fuelType (required)
          merged.monthlyRate ?? 0,                  // 25. monthlyRate
          toTimestamp(merged.stkExpiration),        // 26. stkExpiration
          merged.note || '',                        // 27. note
          merged.service || '',                     // 28. service
          merged.statusNote || '',                  // 29. statusNote
          merged.gpsLatitude ?? 0,                  // 30. gpsLatitude
          merged.gpsLongitude ?? 0,                 // 31. gpsLongitude
          merged.manualAddress || null,             // 32. manualAddress
          merged.technicalPapersUrl || null,        // 33. technicalPapersID (UUID)
          merged.agreementPapersUrl || null,        // 34. agreementPapersID (UUID)
          merged.kmAtYearStart ?? 0,                // 35. kmAtYearStart
          merged.status || 'AVAILABLE',             // 36. status (CarStatus enum)
          toTimestamp(merged.tyreChangingAppointment),  // 37. tyreChangingAppointment
          merged.contractNumber || null,               // 38. contractNumber
          merged.groupId || null,                      // 39. groupId
          merged.shortTerm ?? false,                    // 40. shortTerm 
          merged.customData ? JSON.stringify(merged.customData) : null   // 41. customData


        )

        if (statusChanged) {
          try {
            await api.cars.createRentalCarEvent(id, 'STATUS_CHANGE',
              JSON.stringify({
                fromStatus: previousStatus,
                toStatus: nextStatus,
                shortTerm: !!merged.shortTerm
              }), Date.now(), null
            )

            await this.fetchRentalCarEvents(api, id)
          } catch (eventError) {
            console.error('[carStore] Failed to create rental car status event:', eventError)
          }
        }
        // Fetch fresh data from BE to ensure state consistency
        return await fetchAndCacheCar(api, id, this.cars)
      } catch (e) {
        console.error('[carStore] Failed to update car:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    async fetchRentalCarEvents(api: API, rentalCarId: string): Promise<RentalCarEvent[]> {
      if (!api?.cars) {
        throw new Error('Car API not avaliable')
      }
      try {
        const response = await api.cars.getRentalCarEventsByRentalCarId(rentalCarId)
        const rawEvents = Array.isArray((response as any).value)
          ? (response as any).value
          : Array.isArray(response)
            ? response
            : []

        const events = rawEvents as RentalCarEvent[]
        this.rentalCarEvents[rentalCarId] = events

        return events
      } catch (e) {
        console.error('[carStore] Failed to fetch rental car events:', e)
        this.rentalCarEvents[rentalCarId] = []
        return []
      }
    }

  }
})
